'use strict'

// How a relationship column names a related row, which links a save may
// keep, and which related rows a cascade filter is allowed to offer.

const AGGREGATION = /\b(?:SUM|COUNT|AVERAGE|AVG|MIN|MAX)\s*\(([^()]*)\)/gi

function bridgeKeys (ownerSheet, relatedSheet) {

  if (ownerSheet && relatedSheet && ownerSheet !== relatedSheet) {

    return { source: ownerSheet, target: relatedSheet }
  }

  return { source: `${ownerSheet}_source`, target: `${ownerSheet}_target` }
}

function relationshipChoice (relationshipType) {

  return relationshipType === 'oneToMany' || relationshipType === 'manyToMany' ? 'multiple' : 'single'
}

function replacesThisRow (relationshipType) {

  return relationshipType === 'oneToOne' || relationshipType === 'manyToOne'
}

function constrainsTarget (relationshipType) {

  return relationshipType === 'oneToOne' || relationshipType === 'oneToMany'
}

function displayFormulaColumns (columns) {

  return (columns || []).filter((column) => column && column.type !== 'password' && column.type !== 'file')
}

function relationshipDisplayLabel (formula, value, id) {

  if (!String(formula || '').trim()) return String(id)

  const text = value == null ? '' : String(value).trim()

  return text || String(id)
}

function disambiguateRelationshipLabels (entries) {

  const counts = new Map()

  for (const entry of entries || []) {

    counts.set(entry.label, (counts.get(entry.label) || 0) + 1)
  }

  return (entries || []).map((entry) => {

    if ((counts.get(entry.label) || 0) < 2) return entry

    return { ...entry, label: `${entry.label} (${entry.id})` }
  })
}

function takenLinkMessage (label) {

  return `${label} already links to that record`
}

function cascadeParentFields (filters) {

  const fields = []

  for (const filter of filters || []) {

    if (!filter || !filter.valueFromField) continue
    if (!fields.includes(filter.valueFromField)) fields.push(filter.valueFromField)
  }

  return fields
}

function submittedIds (value) {

  if (value == null || value === '') return []

  if (typeof value === 'string') {

    const text = value.trim()

    if (!text) return []

    if (text[0] === '[' || text[0] === '{') {

      try {

        return submittedIds(JSON.parse(text))
      } catch (err) {

        return []
      }
    }

    const numeric = Number(text)

    return Number.isInteger(numeric) ? [numeric] : []
  }

  if (Array.isArray(value)) {

    const ids = []

    for (const item of value) ids.push(...submittedIds(item))

    return [...new Set(ids)]
  }

  if (typeof value === 'number' && Number.isInteger(value)) return [value]

  if (typeof value === 'object') {

    if (value.id == null || value.id === '') return []

    const numeric = Number(value.id)

    return Number.isInteger(numeric) ? [numeric] : []
  }

  return []
}

function isEmptyFilterValue (value) {

  if (value == null || value === '') return true
  if (Array.isArray(value)) return submittedIds(value).length === 0

  return false
}

function cascadeParentsEmpty (filters, row) {

  const fields = cascadeParentFields(filters)

  if (!fields.length) return false

  return fields.some((field) => isEmptyFilterValue(row && row[field]))
}

function cascadeBoundValue (filter, value) {

  if (filter && filter.dataType === 'relationship') {

    return submittedIds(value).map((id) => ({ id }))
  }

  if (Array.isArray(value)) {

    const first = value[0]

    if (first && typeof first === 'object') return first.id == null ? '' : first.id

    return first == null ? '' : first
  }

  if (value && typeof value === 'object') return value.id == null ? '' : value.id

  return value == null ? '' : value
}

function bindCascadeFilters (filters, row) {

  return (filters || []).map((filter) => {

    if (!filter || !filter.valueFromField) return filter ? { ...filter } : filter

    const next = { ...filter, value: cascadeBoundValue(filter, row && row[filter.valueFromField]) }

    delete next.valueFromField

    return next
  })
}

// null means the cascade does not restrict ids. An array is the filters to run.
// block means the parent is empty and the dropdown has no choices.
function cascadePlan (column, row) {

  const filters = (column && column.cascadeFilters) || []

  if (!filters.length) return { block: false, filters: null }

  if (cascadeParentsEmpty(filters, row)) {

    if (!column.showAllUntilParent) return { block: true, filters: [] }

    const staticFilters = filters.filter((filter) => filter && !filter.valueFromField)

    return { block: false, filters: staticFilters }
  }

  return { block: false, filters: bindCascadeFilters(filters, row) }
}

function normalizeRelationshipSettings (settings) {

  const source = settings || {}
  const filters = Array.isArray(source.cascadeFilters) ? source.cascadeFilters.filter(Boolean).map((filter) => ({ ...filter })) : []
  const addFields = Array.isArray(source.addFields)
    ? source.addFields.map((field) => String(field || '').trim()).filter(Boolean)
    : []

  return {
    displayFormula: source.displayFormula == null ? '' : String(source.displayFormula),
    cascadeFilters: filters,
    cascadeMatchMode: source.cascadeMatchMode === 'any' ? 'any' : 'all',
    showAllUntilParent: !!source.showAllUntilParent,
    allowAdd: !!source.allowAdd,
    addFields
  }
}

function storedRelationshipFields (columnType, settings) {

  if (columnType !== 'relationship') {

    return {
      displayFormula: '',
      cascadeFilters: [],
      cascadeMatchMode: 'all',
      showAllUntilParent: false,
      allowAdd: false,
      addFields: []
    }
  }

  return normalizeRelationshipSettings(settings)
}

const OMIT_FROM_ADD = new Set(['formula', 'sequence'])

function relationshipAddFields (columns, listed) {

  const wanted = new Set((listed || []).map((field) => String(field)))
  const fields = []
  const seen = new Set()

  for (const column of columns || []) {

    if (!column || !column.uniqueTitle || seen.has(column.uniqueTitle)) continue
    if (OMIT_FROM_ADD.has(column.type)) continue
    if (!column.required && !wanted.has(column.uniqueTitle)) continue

    seen.add(column.uniqueTitle)
    fields.push(column)
  }

  return fields
}

function aggregationWalksColumn (formula, columnName) {

  if (!formula || !columnName) return false

  AGGREGATION.lastIndex = 0

  let match

  while ((match = AGGREGATION.exec(formula))) {

    const args = match[1]

    if (
      args.includes(`{${columnName}}`) ||
      args.includes(`{${columnName}.`) ||
      args.includes(`.${columnName}.`) ||
      args.includes(`.${columnName}}`)
    ) return true
  }

  return false
}

function excludeSelfFromSet (ids, recordId) {

  return (ids || []).filter((id) => Number(id) !== Number(recordId))
}

function relationshipUniqueTitleConflict (spreadsheets, uniqueTitle, except) {

  const name = String(uniqueTitle || '').trim()

  if (!name) return null

  const skipSheet = except && except.spreadSheetUniqueTitle
  const skipColumn = except && except.columnUniqueTitle

  for (const sheet of spreadsheets || []) {

    if (!sheet) continue

    for (const column of sheet.columns || []) {

      if (!column || column.type !== 'relationship' || column.uniqueTitle !== name) continue
      if (sheet.uniqueTitle === skipSheet && column.uniqueTitle === skipColumn) continue

      return {
        uniqueTitle: name,
        spreadSheetUniqueTitle: sheet.uniqueTitle,
        spreadSheetTitle: sheet.title || sheet.uniqueTitle,
        columnTitle: column.title || column.uniqueTitle
      }
    }
  }

  return null
}

function relationshipUniqueTitleConflictMessage (conflict) {

  const where = conflict.spreadSheetTitle || conflict.spreadSheetUniqueTitle

  return `A relationship named ${conflict.uniqueTitle} already exists on ${where}. Relationship names have to be unique in the application, so this was not saved.`
}

function rowContextFromValues (values) {

  const row = {}

  for (const [key, value] of Object.entries(values || {})) {

    if (Array.isArray(value) || (value && typeof value === 'object' && value.id != null)) {

      row[key] = submittedIds(value)
      continue
    }

    row[key] = value
  }

  return row
}

module.exports = {
  bridgeKeys,
  relationshipChoice,
  replacesThisRow,
  constrainsTarget,
  displayFormulaColumns,
  relationshipDisplayLabel,
  disambiguateRelationshipLabels,
  takenLinkMessage,
  cascadeParentFields,
  submittedIds,
  isEmptyFilterValue,
  cascadeParentsEmpty,
  bindCascadeFilters,
  cascadePlan,
  normalizeRelationshipSettings,
  storedRelationshipFields,
  relationshipAddFields,
  aggregationWalksColumn,
  excludeSelfFromSet,
  relationshipUniqueTitleConflict,
  relationshipUniqueTitleConflictMessage,
  rowContextFromValues
}
